import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Instagram, Twitter, Music, CalendarDays, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import { Analytics } from "@vercel/analytics/react"
import { textScrollAnimation, fadeInUp } from './utils/animations';
import Shop from './components/sections/Shop';
import './styles/App.css';
import { Gift } from 'lucide-react'; // Add this for the fundraiser icon
import FundraiserPage from './components/pages/FundraiserPage';
import EventsPage from './components/pages/EventsPage';
import Footer from './components/sections/Footer';
import ProductDetail from './components/sections/ProductDetail';
import CheckoutSuccess from './components/pages/CheckoutSuccess';
import CheckoutCancel from './components/pages/CheckoutCancel';




// Home component for the main page content
const Home = ({ isMobile }) => (
  <>
    <section className="hero-section">
      <div className="hero-content">
        <div className="hero-text">
          <h2>About SOUF</h2>
          <p>Sending Out Unique Frequencies (SOUF) represents more than music – it's a movement born in the Bay Area, blending street culture with digital innovation. Through authentic storytelling and groundbreaking sounds, SOUF pushes the boundaries of contemporary hip-hop.</p>
          <div className="hero-cta">
            <Link to="/members" className="cta-button">
              Join the Movement
              <span className="cta-description">Exclusive Access & Early Releases</span>
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Music Section */}
    <section id="music" className="section music-section">
      <h2>Latest Music</h2>
      {/* Spotify Playlist Embed */}
      <div className="spotify-embed">
        <iframe 
          title="Sonny.SOUF Spotify Playlist"
          src="https://open.spotify.com/embed/artist/6SkhrvUnO22BJkOp4Sal0q?utm_source=generator" 
          width="100%" 
          height="352" 
          frameBorder="0" 
          allowfullscreen="" 
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
          loading="lazy"
        ></iframe>
      </div>

      {/* Album Grid */}
      <div className="album-grid">
        <div className="album-card">
          <img src="/gabe_sage.jpg" alt="Gabe The Sage Sonny The Mage" />
          <div className="album-info">
            <h3>Gabe The Sage Sonny The Mage</h3>
            <p>Featured: "Go For It"</p>
            <a href="https://open.spotify.com/track/7usAuV4PV84H2DPfi32CKG" target="_blank" rel="noopener noreferrer" className="play-button">Play</a>
          </div>
        </div>

        <div className="album-card">
          <img src="/nintendos.jpg" alt="Nintendos & Extendos" />
          <div className="album-info">
            <h3>Nintendos & Extendos</h3>
            <p>Featured: "Gameboy"</p>
            <a href="https://open.spotify.com/track/6mLQduC2IcVTzUXp2XBlwk" target="_blank" rel="noopener noreferrer" className="play-button">Play</a>
          </div>
        </div>

        <div className="album-card">
          <img src="/parlay.jpg" alt="Parlay" />
          <div className="album-info">
            <h3>Parlay</h3>
            <p>Featured: "Dime Pass"</p>
            <a href="https://open.spotify.com/track/2RAD3Ybh4AcWfOZfGwT4E4" target="_blank" rel="noopener noreferrer" className="play-button">Play</a>
          </div>
        </div>

        <div className="album-card">
          <img src="/entendre_sideB.jpg" alt="Entendre Side B" />
          <div className="album-info">
            <h3>Entendre Side B</h3>
            <p>Featured: "Options"</p>
            <a href="https://open.spotify.com/track/7hMeB5cVEApIo4QwVr9gGF" target="_blank" rel="noopener noreferrer" className="play-button">Play</a>
          </div>
        </div>
      </div>
    </section>
  </>
);

function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Router>
      <div className="app-wrapper">
        <div className="content-wrapper">
          {/* Social Bar */}
          <div className="social-bar">
            <a href="https://www.instagram.com/sonny.souf" target="_blank" rel="noopener noreferrer" className="social-link">
              <Instagram size={24} />
            </a>
            <a href="https://x.com/SonnySOUF" target="_blank" rel="noopener noreferrer" className="social-link">
              <Twitter size={24} />
            </a>
            <a href="https://open.spotify.com/artist/6SkhrvUnO22BJkOp4Sal0q" target="_blank" rel="noopener noreferrer" className="social-link">
              <Music size={24} />
            </a>
            <Link to="/shop" className="social-link shop-link">
              <ShoppingBag size={24} />
            </Link>
          </div>

          <header>
            <Link to="/">
              <motion.div 
                className="logo-container"
                variants={textScrollAnimation.logoPulse}
                initial="initial"
                animate="animate"
              >
                <img src="/thumbnail_SOUF final BW.png" alt="Sonny.Souf Logo" className="logo" />
              </motion.div>
            </Link>
            
            {isMobile ? (
              <div className="title-container">
                <motion.h1 
                  className="scrolling-title"
                  variants={textScrollAnimation.marquee}
                  animate="animate"
                >
                  SONNY.SOUF • SONNY.SOUF • SONNY.SOUF
                </motion.h1>
              </div>
            ) : (
              <motion.h1
                variants={fadeInUp}
                initial="initial"
                animate="animate"
              >
                SONNY.SOUF
              </motion.h1>
            )}
            
            <nav>
              <Link to="/#music">
                <Music size={16} className="icon" />
                Music
              </Link>
              <Link to="/events">
                <CalendarDays size={16} className="icon" />
                Events
              </Link>
              <Link to="/fundraiser" className="shop-nav-link bg-gradient-to-r from-[#805AD5] to-[#FF4D00]">
                <Gift size={16} className="icon" />
                Fundraiser
              </Link>
              <Link to="/shop" className="shop-nav-link">
                <ShoppingBag size={16} className="icon" />
                Shop
              </Link>
            </nav>
          </header>

          <Routes>
            <Route path="/" element={<Home isMobile={isMobile} />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:productId" element={<ProductDetail />} />
            <Route path="/fundraiser" element={<FundraiserPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/success" element={<CheckoutSuccess />} />
            <Route path="/cancel" element={<CheckoutCancel />} />
          </Routes>

          {/* Footer */}
          <Footer/>
        </div>
      </div>
    </Router>
  );
}

export default App;
